import React, { useCallback, useEffect, useState } from 'react';
import { AlertOctagon, ChevronDown, CloudOff } from 'lucide-react';
import VisitTimeDialog from '../visit/VisitTimeDialog';
import { closeVisit } from '../../services/patientmr/visits.service';
import { useToast } from '../common/Toast';
import { isApiError } from '../../dtos/common/api.dto';
import { usePermission } from '../../hooks/usePermission';
import { ChartReviewService } from '../../services/patientmr/chartReview.service';
import { DocStatusPill } from '../common/Pills';
import { openPatientChartById } from '../../constants/navigationEvents';
import { createPermissionString as perm } from '../../types/permissions';
import ShiftCard from './ShiftCard';
import { ScheduleService, scheduleErrorMessage } from '../../services/scheduling';
import type { AbandonedVisit, BoardColumn, OfflineReviewItem, Shift, TodayBoard as Board } from '../../dtos/scheduling/schedule.dto';
import { longDateLabel } from './scheduleUtils';

const COLUMNS: { key: BoardColumn; title: string; tone: string; empty: string }[] = [
  { key: 'LATE', title: 'Late', tone: 'text-red-700', empty: 'No late shifts' },
  { key: 'UNSTAFFED', title: 'Unstaffed', tone: 'text-red-700', empty: 'Every shift has a clinician' },
  { key: 'UPCOMING', title: 'Upcoming', tone: 'text-gray-800', empty: 'Nothing else today' },
  { key: 'IN_PROGRESS', title: 'In progress', tone: 'text-indigo-700', empty: 'No visits in progress' },
  { key: 'DONE', title: 'Done / cancelled', tone: 'text-gray-500', empty: '—' },
];

const REFRESH_MS = 60_000;

export interface TodayBoardProps {
  date: string;
  isMobile: boolean;
  refreshKey: number;
  onOpen: (shift: Shift) => void;
  onAssign: (shift: Shift) => void;
  /** Phase 16C: open a flagged offline visit (patient chart) */
  onOpenVisit?: (patientId: string, visitId: string) => void;
}

/** Plain-language names for 16B syncFlags. */
export const OFFLINE_FLAG_LABELS: Record<string, string> = {
  PATIENT_ON_HOLD: 'Patient on hold',
  OVERLAP: 'Overlaps another visit',
  OVERLAP_SELF: 'Overlaps own visit',
  SHIFT_UNMATCHED: 'No matching shift',
  PA_NOT_COVERED: 'Not covered by an authorization',
  TIME_UNVERIFIED: 'Device time unverified (EVV review)',
};

/** "Offline visits to review" (16B B9): styled like the abandoned-visit banner. */
export const OfflineReviewBanner: React.FC<{ items: OfflineReviewItem[]; onOpenVisit?: (patientId: string, visitId: string) => void }> = ({ items, onOpenVisit }) => {
  if (!items.length) return null;
  return (
    <section aria-label="Offline visits to review" className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-900"><CloudOff size={16} aria-hidden="true" />{items.length} offline visit{items.length === 1 ? '' : 's'} to review</h2>
      <ul className="mt-2 space-y-2">
        {items.map((v) => (
          <li key={v.visitId} className="flex flex-wrap items-center gap-2 text-sm text-amber-950">
            <span className="flex-1">
              {v.patientName || 'Patient'}{v.providerName ? ` · ${v.providerName}` : ''}{v.startedAt ? ` · ${new Date(v.startedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
              <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
                {v.flags.map((f) => <span key={f} className="rounded border border-amber-400 bg-white px-1.5 py-0.5 text-xs font-medium">{OFFLINE_FLAG_LABELS[f] || f}</span>)}
              </span>
            </span>
            {onOpenVisit && <button type="button" onClick={() => onOpenVisit(v.patientId, v.visitId)} className="min-h-[40px] rounded-md border border-amber-600 bg-white px-3 text-sm font-semibold text-amber-900">Open visit</button>}
          </li>
        ))}
      </ul>
    </section>
  );
};

/**
 * Coordinator Today board (plan §6.1): what needs attention now, by column. Late = 15+ minutes
 * past the start with no visit; Unstaffed = no clinician. Refreshes every minute. On a phone the
 * columns stack as collapsible sections, with the ones needing action open.
 */
const TodayBoard: React.FC<TodayBoardProps> = ({ date, isMobile, refreshKey, onOpen, onAssign, onOpenVisit }) => {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ DONE: true });
  const { showToast } = useToast();
  const canClose = usePermission(perm('patientmr:phi:visit:correctall'));
  // Phase 18U U19: supervisors see which of today's charts wait for review (U16 route, same gate)
  const canReview = usePermission(perm('patientmr:phi:supervisor:approve'));
  const [reviewStatus, setReviewStatus] = useState<Record<string, string>>({});
  const [closing, setClosing] = useState<AbandonedVisit | null>(null);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBoard(await ScheduleService.today(date));
      setError(null);
    } catch (e) {
      setError(scheduleErrorMessage(e, 'Could not load today\'s board'));
    }
    if (canReview) {
      try {
        const { items } = await ChartReviewService.all({ from: date, to: date });
        setReviewStatus(Object.fromEntries(items.map((i) => [i.visitId, i.chartingStatus])));
      } catch {
        // optional: the board works without the review status
      }
    }
  }, [date, canReview]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load, refreshKey]);

  if (error && !board) {
    return <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error} <button type="button" className="ml-2 underline" onClick={load}>Retry</button></div>;
  }
  if (!board) return <div className="p-6 text-sm text-gray-500">Loading today…</div>;

  const total = Object.values(board.counts).reduce((a, b) => a + b, 0);
  const abandoned = board.abandoned || [];
  const offlineBanner = <OfflineReviewBanner items={board.offlineReview || []} onOpenVisit={onOpenVisit} />;

  // VIS-07: visits running 16 h or more, closed here with a corrected end time (in place, D-20)
  const abandonedBanner = abandoned.length > 0 && (
    <section aria-label="Visits running too long" className="mb-3 rounded-lg border border-red-300 bg-red-50 p-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-red-800"><AlertOctagon size={16} />{abandoned.length} visit{abandoned.length === 1 ? '' : 's'} still open after 16+ hours</h2>
      <ul className="mt-2 space-y-2">
        {abandoned.map((a) => (
          <li key={a.visitId} className="flex flex-wrap items-center gap-2 text-sm text-red-900">
            <span className="flex-1">{a.patientName || 'Patient'} · {a.providerName || 'Clinician'} · started {a.startedAt ? new Date(a.startedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '?'} ({a.hours} h)</span>
            {canClose && <button type="button" onClick={() => { setCloseError(null); setClosing(a); }} className="min-h-[40px] rounded-md bg-red-700 px-3 text-sm font-semibold text-white">Close visit…</button>}
          </li>
        ))}
      </ul>
    </section>
  );
  const closeDialog = (
    <VisitTimeDialog
      open={Boolean(closing)}
      mode="close"
      summary={closing ? `${closing.patientName || 'Patient'} · ${closing.providerName || 'Clinician'}. Enter when the visit actually ended.` : ''}
      start={closing?.startedAt || null}
      end={closing?.scheduledEnd || null}
      busy={closeBusy}
      error={closeError}
      onCancel={() => setClosing(null)}
      onConfirm={async ({ endTime }, reason) => {
        if (!closing) return;
        setCloseBusy(true);
        setCloseError(null);
        try {
          await closeVisit(closing.patientId, closing.visitId, endTime, reason);
          showToast('Visit closed', { variant: 'success' });
          setClosing(null);
          load();
        } catch (e) {
          setCloseError(isApiError(e) && e.statusCode < 500 ? e.message : 'Could not close the visit');
        } finally {
          setCloseBusy(false);
        }
      }}
    />
  );

  if (total === 0) {
    return (
      <>
        {abandonedBanner}
        {offlineBanner}
        <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">No shifts on {longDateLabel(board.date)}.</div>
        {closeDialog}
      </>
    );
  }

  return (
    <>
    {abandonedBanner}
    {offlineBanner}
    {closeDialog}
    <div className={isMobile ? 'space-y-3' : 'grid grid-cols-5 gap-3'}>
      {COLUMNS.map((col) => {
        const items = board.columns[col.key] || [];
        const isCollapsed = isMobile && (collapsed[col.key] ?? false);
        return (
          <section key={col.key} className={`min-w-0 rounded-lg bg-gray-50 p-2 ${isMobile ? '' : 'min-h-[60vh]'}`} aria-label={col.title}>
            <button
              type="button"
              className="flex min-h-[44px] w-full items-center justify-between px-1 text-left"
              onClick={() => isMobile && setCollapsed((c) => ({ ...c, [col.key]: !isCollapsed }))}
              aria-expanded={!isCollapsed}
            >
              <span className={`text-sm font-semibold ${col.tone}`}>{col.title} <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-xs text-gray-700">{items.length}</span></span>
              {isMobile && <ChevronDown size={16} className={`text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />}
            </button>
            {!isCollapsed && (
              <div className="mt-1 space-y-2">
                {items.length === 0 && <p className="px-1 py-2 text-xs text-gray-400">{col.empty}</p>}
                {items.map((s) => (
                  <ShiftCard
                    key={s.id}
                    shift={s}
                    onOpen={onOpen}
                    actions={col.key === 'UNSTAFFED' ? (
                      <button type="button" className="min-h-[36px] rounded-md bg-slate-800 px-3 text-xs font-semibold text-white" onClick={(e) => { e.stopPropagation(); onAssign(s); }}>Assign</button>
                    ) : s.visitId && reviewStatus[s.visitId] ? (
                      <span className="flex items-center gap-2" data-testid="today-review">
                        <DocStatusPill status={reviewStatus[s.visitId]} />
                        <button type="button" className="min-h-[36px] rounded-md border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-800"
                          onClick={(e) => { e.stopPropagation(); openPatientChartById(s.patientId, 'Charting', String(s.visitId)); }}>Review</button>
                      </span>
                    ) : undefined}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
    </>
  );
};

export default TodayBoard;
